import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { fetchPaymentStatus } from "../lib/api";
import { money, useShop } from "../context/ShopContext";

const Shell = ({ children }) => (
  <div className="min-h-[80vh] grid place-items-center bg-cream px-6 py-32">
    <div className="text-center max-w-lg">{children}</div>
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useShop();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return setState("error");
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await fetchPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          clearCart();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) return setState("error");
      } catch {
        if (tries.current > 3) return setState("error");
      }
      if (tries.current > 12) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (state === "checking") {
    return (
      <Shell>
        <Loader2 size={32} className="text-gold mx-auto animate-spin" />
        <h1 className="font-serif text-3xl text-ink mt-8" data-testid="payment-checking">Confirming your payment…</h1>
        <p className="text-ink-muted mt-3">This usually takes a few seconds. Please don't close this window.</p>
      </Shell>
    );
  }

  if (state === "paid") {
    return (
      <Shell>
        <CheckCircle2 size={38} strokeWidth={1.3} className="text-gold mx-auto" />
        <h1 className="font-serif text-4xl md:text-5xl text-ink mt-8" data-testid="payment-success">Thank you — your order is in.</h1>
        <p className="text-ink-muted mt-5 leading-relaxed">
          Order <span className="text-ink">{order.order_number}</span> for {money(order.total)} is confirmed.
          We'll email you a receipt and let you know the moment it ships.
        </p>
        <div className="flex flex-wrap gap-4 justify-center mt-10">
          <Link to="/account" data-testid="success-orders-link"
            className="px-8 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors">
            View your orders
          </Link>
          <Link to="/shop" className="px-8 py-4 border border-[#221d17] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#221d17] hover:text-cream transition-colors">
            Keep shopping
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <XCircle size={36} strokeWidth={1.3} className="text-ink-muted mx-auto" />
      <h1 className="font-serif text-3xl text-ink mt-8" data-testid="payment-error">
        {state === "timeout" ? "Still processing" : "We couldn't confirm that payment"}
      </h1>
      <p className="text-ink-muted mt-4 leading-relaxed">
        {state === "timeout"
          ? "Your bank is taking a little longer than usual. Check your orders in a minute — if it's not there, nothing has been charged."
          : "Nothing has been charged. Please try again, or get in touch and we'll sort it out."}
      </p>
      <div className="flex flex-wrap gap-4 justify-center mt-9">
        <Link to="/account" className="px-8 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em]">Your orders</Link>
        <Link to="/cart" className="px-8 py-4 border border-[#221d17] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em]">Back to bag</Link>
      </div>
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell>
    <XCircle size={36} strokeWidth={1.3} className="text-ink-muted mx-auto" />
    <h1 className="font-serif text-4xl text-ink mt-8" data-testid="payment-cancelled">Payment cancelled</h1>
    <p className="text-ink-muted mt-4 leading-relaxed">
      No charge was made and your bag is exactly where you left it.
    </p>
    <div className="flex flex-wrap gap-4 justify-center mt-9">
      <Link to="/cart" data-testid="cancel-cart-link"
        className="px-8 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors">
        Back to your bag
      </Link>
      <Link to="/shop" className="px-8 py-4 border border-[#221d17] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em]">Keep shopping</Link>
    </div>
  </Shell>
);
